import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';

const SpecsContainer = styled.section`
  min-height: 100vh;
  padding: 5rem 2rem;
  background: linear-gradient(135deg, #581c87 0%, #7c3aed 50%, #8b5cf6 100%);
`;

const Content = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(135deg, #3b82f6, #06b6d4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 800;
`;

const SpecsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`;

const SpecCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 2rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  color: #3b82f6;
  margin-bottom: 1.5rem;
  font-weight: 700;
`;

const SpecTable = styled.div`
  display: grid;
  gap: 1rem;
`;

const SpecRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
`;

const SpecLabel = styled.div`
  color: #94a3b8;
  font-size: 0.9rem;
  font-weight: 500;
`;

const SpecValue = styled.div`
  color: #ffffff;
  font-weight: 600;
  text-align: right;
`;

function TechnicalSpecs() {
  const specifications = [
    {
      category: 'Material Properties',
      specs: [
        { label: 'Primary Material', value: 'EN8D Carbon Steel' },
        { label: 'Tensile Strength', value: '550-700 MPa' },
        { label: 'Yield Strength', value: '≥355 MPa' },
        { label: 'Elongation', value: '≥22%' },
        { label: 'Hardness', value: '160-220 HB' }
      ]
    },
    {
      category: 'Performance Standards',
      specs: [
        { label: 'Primary Standard', value: 'IS 16172:2023' },
        { label: 'International', value: 'ACI 318' },
        { label: 'Strength Rating', value: '125% of rebar' },
        { label: 'Fatigue Life', value: '2+ million cycles' },
        { label: 'Temperature Range', value: '-40°C to +80°C' }
      ]
    },
    {
      category: 'Installation Rates',
      specs: [
        { label: 'Threaded (Experienced)', value: '15-20 /hour' },
        { label: 'Threaded (Average)', value: '10-15 /hour' },
        { label: 'Swaged (Experienced)', value: '20-25 /hour' },
        { label: 'Grout-filled', value: '12-15 /hour' },
        { label: 'Quality Control', value: '99.2% pass rate' }
      ]
    },
    {
      category: 'Cost Analysis',
      specs: [
        { label: 'Threaded Couplers', value: '₹25-65 /unit' },
        { label: 'Swaged Couplers', value: '₹20-45 /unit' },
        { label: 'Material Savings', value: '15-20% typical' },
        { label: 'Labor Savings', value: '30-50% typical' },
        { label: 'Typical ROI', value: '150-300%' }
      ]
    },
    {
      category: 'Quality Metrics',
      specs: [
        { label: 'First Pass Rate', value: '99.2%' },
        { label: 'Return Rate', value: '<0.1%' },
        { label: 'Testing Accuracy', value: '±0.5%' },
        { label: 'Batch Rejection', value: '<0.5%' },
        { label: 'Slip Test Limit', value: '≤0.1mm @40% load' }
      ]
    },
    {
      category: 'Applications',
      specs: [
        { label: 'High-Rise Buildings', value: '✓ Optimal' },
        { label: 'Bridge Construction', value: '✓ Proven' },
        { label: 'Marine Structures', value: '✓ SS316L Grade' },
        { label: 'Seismic Zones', value: '✓ Ductile Design' },
        { label: 'Precast Elements', value: '✓ Fast Assembly' }
      ]
    }
  ];

  return (
    <SpecsContainer id="technical-specs" data-section="technical-specs">
      <Content>
        <SectionTitle
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Technical Specifications
        </SectionTitle>

        <SpecsGrid>
          {specifications.map((category, index) => (
            <SpecCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <CardTitle>{category.category}</CardTitle>
              <SpecTable>
                {category.specs.map((spec, specIndex) => (
                  <SpecRow key={specIndex}>
                    <SpecLabel>{spec.label}</SpecLabel>
                    <SpecValue>{spec.value}</SpecValue>
                  </SpecRow>
                ))}
              </SpecTable>
            </SpecCard>
          ))}
        </SpecsGrid>
      </Content>
    </SpecsContainer>
  );
}

export default TechnicalSpecs;